/**
 * PlaceholderReasoningEngine — a deterministic, LLM-free stub whose only
 * purpose is to prove the scoring loop works end-to-end.
 *
 * This is NOT the real reasoning engine. It intentionally implements only
 * enough logic to exercise every axis the harness scores against:
 *   - joins DemandSignal to VisibilitySignal to find gaps
 *   - respects `constraints` (soft downweight) and disallowed keywords (hard reject)
 *   - emits `insufficient_evidence` when signal is thin
 *   - records `deliberation` (considered + ranked_first_because + rejected)
 *
 * Replace with an LLM-backed implementation once the harness has a stable baseline.
 */

import type {
  Deliberation,
  EngineOutput,
  InsufficientEvidenceNote,
  OpportunityDraft,
  OpportunityHorizon,
  OpportunityLevel,
  RejectedCandidate,
} from "../types";
import type { ReasoningEngine, EngineInput } from "./engine";

const PRESENCE_GAP_SCORE: Record<string, number> = {
  absent: 3,
  thin: 2,
  present: 0.5,
  featured: 0,
};

/** Very small heuristic — does any word in `text` appear in `haystack`? */
function mentions(haystack: string, needles: string[]): boolean {
  const h = haystack.toLowerCase();
  return needles.some((n) => n && h.includes(n.toLowerCase()));
}

function pickHorizon(volume: number): OpportunityHorizon {
  if (volume >= 80) return "this_week";
  if (volume >= 40) return "this_season";
  return "this_year";
}

function pickLevel(score: number): OpportunityLevel {
  if (score >= 6) return "high";
  if (score >= 3) return "medium";
  return "low";
}

export class PlaceholderReasoningEngine implements ReasoningEngine {
  readonly name = "placeholder";
  readonly version = "0.1.0";

  async analyze(input: EngineInput): Promise<EngineOutput> {
    const { business_context: ctx, demand, visibility, provider_versions } = input;

    const considered: Deliberation["considered"] = [];
    const rejected: RejectedCandidate[] = [];
    const insufficient: InsufficientEvidenceNote[] = [];

    // --- Uncertainty over hallucination ---------------------------------
    if (demand.length < 3) {
      insufficient.push({
        area: "demand_intelligence",
        reason:
          "Too few demand signals to reason about local customer behavior with confidence.",
        what_would_unblock_it:
          "Connect a demand provider (Semrush, DataForSEO, or Search Console) with more coverage.",
      });
    }
    if (visibility.length === 0) {
      insufficient.push({
        area: "visibility",
        reason:
          "We can't recommend where to focus until we can see where you're currently visible.",
        what_would_unblock_it: "Add the store's website so we can crawl it.",
      });
    }

    // --- Candidate generation: join demand to visibility ----------------
    type Candidate = {
      title: string;
      demand_term: string;
      volume: number;
      trend: string;
      presence: string;
      visibility_detail?: string;
      score: number;
    };

    const visByTerm = new Map<string, (typeof visibility)[number]>();
    for (const v of visibility) visByTerm.set(v.term.toLowerCase(), v);

    const candidates: Candidate[] = demand.map((d) => {
      const v = visByTerm.get(d.term.toLowerCase());
      const presence = v?.presence ?? "absent";
      const volume = d.monthly_volume ?? d.volume_index ?? 0;
      const gap = PRESENCE_GAP_SCORE[presence] ?? 0;
      const trendBoost = d.trend === "rising" ? 1.5 : d.trend === "falling" ? -1 : 0;
      const localBoost = (d.local_confidence ?? 0.5) * 2;
      const score = gap * (Math.log10(Math.max(volume, 1)) + 1) + trendBoost + localBoost;
      return {
        title: toTitle(d.term),
        demand_term: d.term,
        volume,
        trend: d.trend ?? "flat",
        presence,
        visibility_detail: v?.detail,
        score,
      };
    });

    for (const c of candidates) {
      considered.push({
        title: c.title,
        note: `presence=${c.presence}, volume=${c.volume}, trend=${c.trend}, score=${c.score.toFixed(2)}`,
      });
    }

    // --- Hard rejects: disallowed / constraint conflicts ----------------
    // NOTE: Placeholder only. The real engine will reason semantically about
    // whether a recommendation violates a constraint — not by keyword match.
    const constraintNeedles = ctx.constraints.flatMap((c) =>
      c.toLowerCase().match(/\b(discount|sale|promo|wedding|personalization|weekend)\b/g) ?? [],
    );

    const kept: Candidate[] = [];
    for (const c of candidates) {
      if (mentions(c.title + " " + (c.visibility_detail ?? ""), constraintNeedles)) {
        rejected.push({
          title: c.title,
          reason: `Conflicts with a stated constraint (${ctx.constraints.join("; ")}).`,
        });
        continue;
      }
      if (c.presence === "featured") {
        rejected.push({
          title: c.title,
          reason: "Already featured — better spend attention on gaps than on wins.",
        });
        continue;
      }
      if (c.score <= 0) {
        rejected.push({
          title: c.title,
          reason: "Signal too weak to justify a recommendation right now.",
        });
        continue;
      }
      kept.push(c);
    }

    kept.sort((a, b) => b.score - a.score);
    const top = kept.slice(0, 5);

    // --- Shape into Opportunities ---------------------------------------
    const opportunities: OpportunityDraft[] = top.map((c, i) => {
      const strengthMatch = ctx.strengths.find((s) =>
        s.toLowerCase().split(/\s+/).some((w) => w.length > 3 && c.title.toLowerCase().includes(w)),
      );
      const goalMatch = ctx.goals[0];

      return {
        priority_rank: i + 1,
        horizon: pickHorizon(c.volume),
        title: c.title,
        what_we_found:
          `Local shoppers are searching for "${c.demand_term}" (${describeVolume(c.volume)}, ${c.trend}), ` +
          `and your store is currently ${c.presence} for it${c.visibility_detail ? ` — ${c.visibility_detail}` : ""}.`,
        why_it_matters:
          strengthMatch
            ? `This overlaps with a real strength ("${strengthMatch}"), so closing the visibility gap should be a fast win.`
            : `There's real demand you're not capturing, and that gap will keep sending customers to other stores until it's closed.`,
        recommended_next_move:
          c.presence === "absent"
            ? `Create a dedicated destination for "${c.demand_term}" — a page, guide, or featured collection — and link it from your homepage.`
            : `Deepen your existing "${c.demand_term}" presence: expand the page, add product photos, and mention it in your next email.`,
        estimated_business_outcome: goalMatch
          ? `Directional lift toward "${goalMatch}"; too early to promise a number.`
          : null,
        impact: pickLevel(c.score),
        effort: c.presence === "absent" ? "medium" : "low",
        confidence: c.volume >= 30 ? "high" : "medium",
        affects: ["website", "merchandising"],
        evidence: [
          {
            evidence_type: "demand",
            provider: Object.keys(provider_versions)[0] ?? "unknown",
            detail: { term: c.demand_term, volume: c.volume, trend: c.trend },
            summary: `Demand signal: "${c.demand_term}"`,
          },
          {
            evidence_type: "visibility",
            provider: "site_crawl",
            detail: { presence: c.presence, note: c.visibility_detail },
            summary: `Visibility on-site: ${c.presence}`,
          },
        ],
      };
    });

    const deliberation: Deliberation = {
      considered,
      ranked_first_because:
        top[0]
          ? `"${top[0].title}" pairs the largest demand-vs-visibility gap with a trend the store can act on this ${pickHorizon(top[0].volume).replace("this_", "")}.`
          : undefined,
      rejected,
    };

    // If no top candidate cleared the bar, be honest about it.
    if (opportunities.length === 0) {
      insufficient.push({
        area: "opportunities",
        reason:
          "No candidate met the bar for a confident recommendation given current evidence and constraints.",
        what_would_unblock_it:
          "Broader demand coverage, or an updated business context that opens more options.",
      });
    }

    return {
      engine_version: `${this.name}@${this.version}`,
      provider_versions,
      opportunities,
      deliberation,
      insufficient_evidence: insufficient,
    };
  }
}

function toTitle(term: string): string {
  return term
    .split(/\s+/)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function describeVolume(v: number): string {
  if (v >= 80) return "high local interest";
  if (v >= 40) return "steady local interest";
  if (v >= 10) return "modest local interest";
  return "early signal";
}